import React, { useState, useEffect } from "react";
import { Star } from "lucide-react";
import { t as staticT } from "../../i18n/translations";
import { useLanguage } from "../../contexts/LanguageContext";
import api from "../../api/axios";

const AVATAR_COLORS = [
  "bg-red-100 text-red-700",
  "bg-teal-100 text-teal-700",
  "bg-blue-100 text-blue-700",
  "bg-amber-100 text-amber-700",
  "bg-green-100 text-green-700",
];

const FALLBACK_TESTIMONIALS = [
  { avatarInitials: "PS", name: staticT("testimonials.t1Name"), location: staticT("testimonials.t1Role"), rating: 5, text: staticT("testimonials.t1Quote") },
  { avatarInitials: "RK", name: staticT("testimonials.t2Name"), location: staticT("testimonials.t2Role"), rating: 5, text: staticT("testimonials.t2Quote") },
  { avatarInitials: "AD", name: staticT("testimonials.t3Name"), location: staticT("testimonials.t3Role"), rating: 5, text: staticT("testimonials.t3Quote") },
];

const TestimonialsSection = () => {
  const { t } = useLanguage();
  const [testimonials, setTestimonials] = useState(FALLBACK_TESTIMONIALS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/public/testimonials")
      .then((res) => {
        const data = res.data?.testimonials;
        if (Array.isArray(data) && data.length > 0) {
          setTestimonials(data);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <section className="bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-2 bg-red-50 text-red-700 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider mb-4 border border-red-100">
            {t("testimonials.badge")}
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
            {t("testimonials.title")}
          </h2>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-slate-100 rounded-lg p-7 border border-slate-200 animate-pulse h-48" />
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((item, idx) => (
              <div
                key={item._id || idx}
                className="bg-slate-50 rounded-lg p-7 border border-slate-200 hover:shadow-md transition-colors duration-150 flex flex-col"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(item.rating || 5)].map((_, i) => (
                    <Star key={i} size={16} className="text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-slate-700 leading-relaxed flex-1 mb-6 text-sm">"{item.text}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-slate-200">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${AVATAR_COLORS[idx % AVATAR_COLORS.length]}`}>
                    {item.avatarInitials || (item.name ? item.name.substring(0, 2).toUpperCase() : "?")}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900">{item.name}</p>
                    <p className="text-xs text-slate-500">{item.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default TestimonialsSection;
